import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Receipt, Target, TrendingUp, Scale, Clock } from 'lucide-react'
import { api } from '../lib/api'
import MetricCard from '../components/MetricCard'

const DAYS_OPTIONS = [7, 14, 30, 60, 90]

// MetricCard's real prop names are title/color/icon (see components/MetricCard.jsx).
// This page was calling it with label/variant and no icon — none of those match, so
// every card silently fell back to MetricCard's blue default and a blank icon slot.
// 'positive'/'negative'/'neutral' don't exist in MetricCard's color themes either —
// mapped to green/red/slate below.
const VARIANT_COLOR = { positive: 'green', negative: 'red', neutral: 'slate' }

function pct(v) {
  if (v == null) return '—'
  const n = Number(v)
  return `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`
}

function ExitReasonBadge({ reason }) {
  const styles = {
    TRAILING_STOP: 'bg-rose-900/40 text-rose-300 border-rose-700/50',
    SIGNAL:        'bg-emerald-900/40 text-emerald-300 border-emerald-700/50',
    DISASTER:      'bg-orange-900/40 text-orange-300 border-orange-700/50',
  }
  const cls = styles[reason] || 'bg-slate-800 text-slate-400 border-slate-700'
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded border text-[10px] font-mono ${cls}`}>
      {reason || '—'}
    </span>
  )
}

export default function Performance() {
  const [days, setDays] = useState(30)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    setLoading(true)
    setError(null)
    api.performance(days)
      .then(d => { setData(d); setLoading(false) })
      .catch(e => { setError(e.message); setLoading(false) })
  }, [days])

  const s = data?.summary

  return (
    <div className="space-y-6">

      {/* Header + lookback selector */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-slate-200 font-semibold text-sm tracking-widest"
              style={{ fontFamily: 'Rajdhani, sans-serif' }}>
            TRADE PERFORMANCE
          </h2>
          <p className="text-slate-500 text-xs mt-0.5">Live P&L from trade_entries / trade_exits (Tweak 8)</p>
        </div>
        <div className="flex items-center gap-1">
          {DAYS_OPTIONS.map(d => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-2.5 py-1 rounded text-xs font-mono transition-colors ${
                days === d
                  ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/40'
                  : 'text-slate-500 hover:text-slate-300 border border-transparent'
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="rounded-lg p-4 bg-rose-900/20 border border-rose-700/40 text-rose-300 text-sm font-mono">
          {error}
        </div>
      )}

      {loading && !data && (
        <div className="text-slate-500 text-sm font-mono animate-pulse">Loading...</div>
      )}

      {s && (
        <>
          {/* KPI row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3">
            <MetricCard title="Trades" value={s.total_trades} color={VARIANT_COLOR.neutral} icon={<Receipt className="h-5 w-5" />} />
            <MetricCard
              title="Win Rate"
              value={`${s.win_rate_pct?.toFixed(1)}%`}
              subtitle={`${s.wins}W / ${s.losses}L`}
              color={VARIANT_COLOR[s.win_rate_pct >= 45 ? 'positive' : s.win_rate_pct >= 35 ? 'neutral' : 'negative']}
              icon={<Target className="h-5 w-5" />}
            />
            <MetricCard
              title="Avg P&L"
              value={pct(s.avg_pnl_pct)}
              color={VARIANT_COLOR[s.avg_pnl_pct >= 0 ? 'positive' : 'negative']}
              icon={<TrendingUp className="h-5 w-5" />}
            />
            <MetricCard
              title="Profit Factor"
              value={s.profit_factor >= 999 ? '∞' : s.profit_factor?.toFixed(2)}
              subtitle="gross wins / losses"
              color={VARIANT_COLOR[s.profit_factor >= 1.3 ? 'positive' : s.profit_factor >= 1.0 ? 'neutral' : 'negative']}
              icon={<Scale className="h-5 w-5" />}
            />
            <MetricCard
              title="Avg Hold"
              value={s.avg_hold_minutes ? `${Math.round(s.avg_hold_minutes)}m` : '—'}
              color={VARIANT_COLOR.neutral}
              icon={<Clock className="h-5 w-5" />}
            />
          </div>

          {/* Win / Loss detail */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg p-4 border border-slate-800" style={{ background: '#0d1321' }}>
              <p className="text-slate-500 text-xs tracking-widest mb-2">AVG WIN</p>
              <p className="text-emerald-400 font-mono font-bold text-lg">{pct(s.avg_win_pct)}</p>
            </div>
            <div className="rounded-lg p-4 border border-slate-800" style={{ background: '#0d1321' }}>
              <p className="text-slate-500 text-xs tracking-widest mb-2">AVG LOSS</p>
              <p className="text-rose-400 font-mono font-bold text-lg">{pct(s.avg_loss_pct)}</p>
            </div>
          </div>

          {/* Exit reason breakdown */}
          {data.by_exit_reason?.length > 0 && (
            <div className="rounded-lg border border-slate-800 overflow-hidden" style={{ background: '#0d1321' }}>
              <div className="px-4 py-3 border-b border-slate-800">
                <p className="text-slate-400 text-xs tracking-widest font-semibold">EXIT REASON BREAKDOWN</p>
              </div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-slate-600 border-b border-slate-800/60">
                    <th className="px-4 py-2 text-left">Reason</th>
                    <th className="px-4 py-2 text-right">Count</th>
                    <th className="px-4 py-2 text-right">Avg P&L</th>
                    <th className="px-4 py-2 text-right">Win Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_exit_reason.map((r, i) => (
                    <tr key={i} className="border-b border-slate-800/30 hover:bg-slate-800/20">
                      <td className="px-4 py-2.5"><ExitReasonBadge reason={r.reason} /></td>
                      <td className="px-4 py-2.5 text-right text-slate-300 font-mono">{r.count}</td>
                      <td className={`px-4 py-2.5 text-right font-mono ${r.avg_pnl_pct >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                        {pct(r.avg_pnl_pct)}
                      </td>
                      <td className="px-4 py-2.5 text-right text-slate-300 font-mono">
                        {r.count > 0 ? `${(r.wins / r.count * 100).toFixed(0)}%` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* By signal type */}
          {data.by_signal?.length > 0 && (
            <div className="rounded-lg border border-slate-800 overflow-hidden" style={{ background: '#0d1321' }}>
              <div className="px-4 py-3 border-b border-slate-800">
                <p className="text-slate-400 text-xs tracking-widest font-semibold">P&L BY SIGNAL TYPE</p>
              </div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-slate-600 border-b border-slate-800/60">
                    <th className="px-4 py-2 text-left">Signal</th>
                    <th className="px-4 py-2 text-right">Trades</th>
                    <th className="px-4 py-2 text-right">Avg P&L</th>
                    <th className="px-4 py-2 text-right">Win Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_signal.map((r, i) => (
                    <tr key={i} className="border-b border-slate-800/30 hover:bg-slate-800/20">
                      <td className="px-4 py-2.5 text-slate-300 font-mono">{r.signal_type}</td>
                      <td className="px-4 py-2.5 text-right text-slate-300 font-mono">{r.count}</td>
                      <td className={`px-4 py-2.5 text-right font-mono ${r.avg_pnl_pct >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                        {pct(r.avg_pnl_pct)}
                      </td>
                      <td className="px-4 py-2.5 text-right text-slate-300 font-mono">
                        {r.count > 0 ? `${(r.wins / r.count * 100).toFixed(0)}%` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Recent closed trades now live on the Trades page (full order history +
              P&L, not just a summary) — link out instead of duplicating the table. */}
          {data.recent_trades?.length > 0 && (
            <Link to="/trades"
              className="flex items-center justify-between rounded-lg border border-slate-800 px-4 py-3 hover:border-slate-700 transition-colors"
              style={{ background: '#0d1321' }}>
              <span className="flex items-center gap-2 text-slate-400 text-xs tracking-widest font-semibold">
                <Receipt className="h-3.5 w-3.5 text-slate-600" />
                RECENT CLOSED TRADES
              </span>
              <span className="text-cyan-400 text-xs font-mono">View full trade history →</span>
            </Link>
          )}

          {s.total_trades === 0 && (
            <div className="text-center py-12 text-slate-600 text-sm font-mono">
              No closed trades in the last {days} days.
              <br />
              <span className="text-xs text-slate-700 mt-1 block">
                Data is recorded from this point forward — historical trades from before Tweak 8 are not backfilled.
              </span>
            </div>
          )}
        </>
      )}
    </div>
  )
}
